import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import TransactionForm from "../components/TransactionForm.jsx";
import { api } from "../lib/api.js";
import { todayISO } from "../lib/money.js";

// Review an OCR'd receipt: image on top, prefilled expense form below.
// Nothing hits the ledger until the user confirms.
export default function ReviewReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api(`/api/receipts/${id}`)
      .then((d) => setReceipt(d.receipt))
      .catch(() => setError("Receipt not found."));
  }, [id]);

  async function discard() {
    if (!window.confirm("Discard this receipt photo?")) return;
    await api(`/api/receipts/${id}`, { method: "DELETE" });
    navigate("/receipts");
  }

  const ocr = receipt?.ocr;
  const lowConfidence = ocr && (ocr.confidence ?? 0) < 0.7;
  const prefill = ocr
    ? {
        type: "expense",
        amount_cents: ocr.total_cents || null,
        date: /^\d{4}-\d{2}-\d{2}$/.test(ocr.date) ? ocr.date : todayISO(),
        account_id: ocr.category_id !== "unknown" ? ocr.category_id : "",
        vendor: ocr.merchant || "",
        payment_method: ocr.payment_method_hint || "",
        memo: ocr.summary || "",
      }
    : null;

  return (
    <Shell title="Review receipt" back>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!receipt && !error && <p className="text-center text-sm text-slate-400">Loading…</p>}

      {receipt && (
        <>
          <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
            <img
              src={`/api/receipts/${id}/image`}
              alt="Receipt"
              className="max-h-72 w-full bg-slate-100 object-contain"
              onError={(e) => {
                // Emailed PDFs/text can't render in <img> — show a labeled placeholder.
                e.currentTarget.outerHTML =
                  '<div class="flex h-32 items-center justify-center bg-slate-100 text-sm text-slate-500">📧 Emailed receipt (PDF/text) — details extracted below</div>';
              }}
            />
          </div>

          {receipt.status === "failed" && (
            <p className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">
              Couldn't read this receipt automatically — fill in the details below.
            </p>
          )}
          {lowConfidence && receipt.status !== "failed" && (
            <p className="rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-800">
              ⚠️ Low confidence read — double-check the amount and date against the photo.
            </p>
          )}

          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <TransactionForm
              prefill={prefill}
              receiptId={id}
              submitLabel="Confirm & file"
              onSaved={() => navigate("/receipts")}
            />
          </div>

          <button onClick={discard} className="w-full rounded-xl border border-red-200 py-3 text-sm font-semibold text-red-600">
            Discard receipt
          </button>
        </>
      )}
    </Shell>
  );
}
